import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Form, Table } from "antd"
import React from "react"

import InputNumber from "../../../../../components/Input/InputNumber.tsx"
import { DocumentCard } from "../../../../../components/Proposal&Contracts/DocumentCard.tsx"
import { validateNumber } from "../../../../../utils/validation/number.ts"
import type {
  ContactFormWarranty,
  ContractFormData
} from "../contract-form-data.tsx"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

export const defaultContractFormWarrantyItems: ContactFormWarranty["items"] = [
  {
    warranty_type: "Labor",
    duration: "2",
    covers: "Workmanship",
    excludes: "Owner-supplied materials"
  },
  {
    warranty_type: "Structural",
    duration: "5",
    covers: "Frame & foundation",
    excludes: "Earthquake, flood, fire"
  },
  {
    warranty_type: "Electrical / Plumbing",
    duration: "1",
    covers: "Rough installation",
    excludes: "Fixtures, client equipment"
  }
]

type FieldType = ContactFormWarranty["items"]

export function ContractWarrantySection() {
  const key: keyof ContractFormData = "warranty"

  return (
    <DocumentCard title="Warranty & Maintenance" name={key}>
      <Form.Item<ContractFormData>
        name={[key, "description"]}
        label="Warranty Description"
      >
        <TextArea placeholder="Enter description" rows={2} />
      </Form.Item>

      <Form.List name={[key, "items"]}>
        {(fields, { add, remove }) => (
          <Table
            pagination={false}
            dataSource={fields}
            rowKey="key"
            bordered
            scroll={{ x: "max-content" }}
            columns={[
              {
                title: "Type",
                dataIndex: "name",
                key: "warranty_type",
                render: (_, field) => (
                  <Form.Item<ContactFormWarranty["items"]>
                    name={[field.name, "warranty_type"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, whitespace: true, message: "Required" }
                    ]}
                  >
                    <TextInput placeholder="Enter type" />
                  </Form.Item>
                )
              },
              {
                title: "Duration",
                width: 185,
                dataIndex: "name",
                key: "duration",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "duration"]}
                    style={{ margin: 0 }}
                    rules={[
                      { required: true, message: "Required" },
                      { validator: validateNumber }
                    ]}
                  >
                    <InputNumber
                      min={1}
                      placeholder="0"
                      className="w-full"
                      max={99999999} // 8 digits before decimal
                      suffix={
                        <Form.Item<ContractFormData> noStyle shouldUpdate>
                          {({ getFieldValue }) => {
                            const val = getFieldValue([
                              key,
                              "items",
                              field.name,
                              "duration"
                            ])
                            return val == 1 ? "year" : "years"
                          }}
                        </Form.Item>
                      }
                    />
                  </Form.Item>
                )
              },
              {
                title: "Covers",
                dataIndex: "name",
                // width: 150,
                key: "covers",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "covers"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <TextInput placeholder="Enter covers" />
                  </Form.Item>
                )
              },
              {
                title: "Excludes",
                dataIndex: "name",
                key: "excludes",
                render: (_, field) => (
                  <Form.Item<FieldType>
                    name={[field.name, "excludes"]}
                    style={{ margin: 0 }}
                    rules={[{ required: false, whitespace: true }]}
                  >
                    <TextInput placeholder="Enter excludes" />
                  </Form.Item>
                )
              },
              {
                title: "",
                width: 50,
                fixed: "right",
                render: (_, field) => (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                  />
                )
              }
            ]}
            footer={() => (
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Warranty Item
              </Button>
            )}
          />
        )}
      </Form.List>
    </DocumentCard>
  )
}
